// File: src/components/TicketPurchase/Ticket.jsx
import React from 'react';
import { LuClock, LuTicket, LuBadgeDollarSign } from 'react-icons/lu';

/* ───────────────────────── palette (aligned with checkout) */
const C = {
  ink:  '#0B0B0B',
  sub:  '#6B7280',
  mid:  '#818793',
  hair: '#EEF0F2',
  white:'#FFFFFF',
  mint: '#10B981',
};

/* ───────────────────────── helpers */
const parseYMD = (str = '') => {
  const [y, m, d] = String(str).split('-').map(Number);
  return y ? new Date(y, (m || 1) - 1, d || 1) : new Date(str || Date.now());
};

const fmtDate = (ymd) => {
  if (!ymd) return '';
  const dt = parseYMD(ymd);
  const wd = dt.toLocaleString('en-US', { weekday: 'short' }); // Wed
  const mo = dt.toLocaleString('en-US', { month: 'short' });   // Aug
  const d  = dt.getDate();
  return `${wd}, ${mo} ${d}`;
};

const toLowerAmPm = (t) => {
  if (!t) return '';
  const [time = '', ap = ''] = String(t).trim().split(/\s+/);
  return `${time.replace(':00','')}${ap.toLowerCase()}`;
};
const timeRange = (s, e) =>
  (s || e) ? `${toLowerAmPm(s)}${s && e ? ' - ' : ''}${toLowerAmPm(e)}` : '';

const splitAddress = (addr = '') => {
  if (!addr) return { street: '', locality: '' };
  const parts = addr.split(',').map(s => s.trim());
  if (parts.length <= 1) return { street: addr, locality: '' };
  return { street: parts.slice(0, 1).join(', '), locality: parts.slice(1).join(', ') };
};

const tzAbbr = (d = new Date()) => {
  try {
    const s = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(d);
    const abbr = s.split(' ').pop();
    return (abbr || '').replace(/[()]/g, '');
  } catch { return ''; }
};

/* ───────────────────────── component */
export default function Ticket({ event = {} }) {
  const {
    name = 'Untitled Event',
    hostedAt = '',
    address = '',
    eventDate = '',
    startTime12 = '',
    endTime12 = '',
    quantity,
    price,
  } = event;

  const { street, locality } = splitAddress(address);
  const dateText  = fmtDate(eventDate);
  const rangeText = timeRange(startTime12, endTime12);
  const qty       = Number(quantity) || 1;
  const priceNum  = Number(price ?? 0);

  // Use event date for timezone note if present
  const tzNote = tzAbbr(parseYMD(eventDate));

  return (
    <section aria-label="Order details" style={S.card}>
      {/* Section label */}
      <div style={S.head}>ORDER DETAILS</div>

      {/* Title + venue */}
      <div style={S.titleBlock}>
        <h2 style={S.title} title={name}>{name}</h2>
        {(hostedAt || street) && (
          <div style={S.venueRow}>
            {hostedAt ? <span style={S.venueStrong}>{hostedAt}</span> : null}
            {(hostedAt && street) ? <span style={S.dot}>•</span> : null}
            {street ? <span style={S.venueLight}>{street}</span> : null}
          </div>
        )}
        {locality ? <div style={S.venueLight}>{locality}</div> : null}
      </div>

      {/* Rows */}
      <div style={S.rows}>
        {(dateText || rangeText) && (
          <>
            <Row
              icon={<LuClock size={18} />}
              chip="Time"
              right={
                <span style={S.rightStrong}>
                  {dateText}
                  {dateText && rangeText ? <span style={S.bullet}> • </span> : null}
                  {rangeText}
                </span>
              }
              rightNote={tzNote || 'LOCAL'}
            />
            <div style={S.hair} />
          </>
        )}

        <Row
          icon={<LuTicket size={18} />}
          chip="Quantity"
          right={<span style={S.qtyBadge}>{qty}</span>}
          rightNote={qty === 1 ? 'ticket' : 'tickets'}
        />
        <div style={S.hair} />

        <Row
          icon={<LuBadgeDollarSign size={18} />}
          chip="Price"
          right={<span style={S.price}>${priceNum.toFixed(2)}</span>}
          rightNote="USD"
        />
      </div>

      {/* bottom rhythm line */}
      <div style={S.divider} />
    </section>
  );
}

/* ───────────────────────── Row subcomponent */
function Row({ icon, chip, right, rightNote }) {
  return (
    <div style={S.row}>
      <div style={S.left}>
        <span style={S.chip}>
          <span style={S.chipIcon}>{icon}</span>
          <span>{chip}</span>
        </span>
      </div>
      <div style={S.rightStack}>
        <div style={S.right}>{right}</div>
        {rightNote ? <div style={S.rightNote}>{rightNote}</div> : null}
      </div>
    </div>
  );
}

/* ───────────────────────── styles */
const S = {
  card: {
    background: C.white,
    borderRadius: 16,
    margin: '0 6px 16px',
    width: 'calc(100% - 16px)',
    boxSizing: 'border-box',
    color: C.ink,
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  head: {
    padding: '14px 16px 0px',
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 12,
    color: '#6B7280',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },

  titleBlock: { padding: '0 16px 8px' },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.15,
    letterSpacing: -0.1,
  },
  venueRow: {
    marginTop: 6,
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    flexWrap: 'wrap',
  },
  venueStrong: { fontSize: 13, fontWeight: 700, color: '#555',lineHeight: 1.5 },
  venueLight:  { fontSize: 13, color: '#555', fontWeight: 500 },
  dot:         { color: C.mid },

  rows: { padding: '4px 8px 0' },

  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    padding: '10px 8px',
  },
  left: { display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 },

  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 10,
    fontWeight: 800,
    color: C.ink,
    background: '#F3F4F6',
    padding: '4px 6px',
    paddingRight: 8,
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginLeft: -4,
  },
  chipIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 0,
    color: '#111827',
  },

  rightStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  right: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  rightNote: {
    fontSize: 10,
    lineHeight: 1,
    color: C.mid,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  rightStrong: { fontSize: 13, fontWeight: 800 },
  bullet: { color: C.mid },

  qtyBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 22,
    height: 22,
    borderRadius: 999,
    color: C.ink,
    fontWeight: 800,
    fontSize: 13,
  },

  price: { fontSize: 13, fontWeight: 900 },

  hair: { borderTop: `1px solid ${C.hair}`, margin: '0 8px' },
  divider: { borderTop: `0px solid ${C.hair}`, width: 'calc(100% - 32px)', margin: '10px auto 0' },
};
